import type React from 'react';
import { useState } from 'react';

import { useGetEvent, useGetFace, useGetFaceAttributeSamples } from '@vlabs/api/luna-api/client';
import type { EventId } from '@vlabs/api/luna-api/model';
import { ButtonSwitch, ButtonSwitchGroup } from '@vlabs/uikit';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import {
  AdditionalPropertiesBySampleId,
  TopMatchCardInfo,
  TopMatchFaceAttributes,
} from '@vlabs/monokit/entities/event-details';
import { GoToCardButton } from '@vlabs/monokit/features/go-to-card';
import { FindSimilarDropdown } from '@vlabs/monokit/features/point/find-similar';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useTopMatchCardPointTranslation } from './i18n';
import st from './TopMatchCardPoint.module.sass';

interface TopMatchCardPointProps extends WithDataTestid, WithClassNameProp {
  eventId: EventId;
  canReadCard?: boolean;
  canViewCardDetails?: boolean;
}

type Tab = 'attributes' | 'additional_properties';

const displayName = 'TopMatchCardPoint';
const testid = buildTestid(displayName);

function TopMatchCardPoint({
  eventId,
  className = undefined,
  'data-testid': dataTestId = undefined,
  canReadCard = true,
  canViewCardDetails = true,
}: TopMatchCardPointProps): React.JSX.Element | null {
  const { t } = useTopMatchCardPointTranslation();
  const [tab, setTab] = useState<Tab>('attributes');
  const { data: event } = useGetEvent(eventId, { target: ['top_match'] });

  // для Point топ-матч показываем только как карточку, нужен face top_match
  const topMatch = event?.top_match;
  const faceId = topMatch && 'face_id' in topMatch ? topMatch.face_id : undefined;
  // external_id проверяем по актуальному лицу
  const { data: face } = useGetFace(
    faceId ?? '',
    { targets: ['external_id'] },
    { query: { enabled: !!faceId } },
  );
  const { data: faceAttributeSamples } = useGetFaceAttributeSamples(faceId ?? '', {
    query: { enabled: !!faceId && tab === 'additional_properties' },
  });
  const externalId = face?.external_id;
  const sampleId = faceAttributeSamples?.samples?.[0];

  if (!canReadCard) return null;
  if (!faceId || !externalId) return null;

  return (
    <div
      className={cn(st.Root, className)}
      {...testid(dataTestId)}
    >
      <h5>{t('title')}</h5>
      <div className={st.Panel}>
        <div className={st.Header}>
          <ButtonSwitchGroup
            onChange={setTab}
            size="small"
            value={tab}
            variant="square"
            {...testid('Tabs', dataTestId)}
          >
            <ButtonSwitch value="attributes">
              {t('attributes')}
            </ButtonSwitch>
            <ButtonSwitch value="additional_properties">
              {t('additional_properties')}
            </ButtonSwitch>
          </ButtonSwitchGroup>

          <div className={st.Actions}>
            {!!canViewCardDetails && (
              <GoToCardButton
                cardId={externalId}
                variant="filled"
                {...testid(dataTestId)}
              />
            )}
            <FindSimilarDropdown
              eventId={eventId}
              type="top match"
              {...testid(dataTestId)}
            />
          </div>
        </div>

        <div className={st.Content}>
          <TopMatchCardInfo
            faceId={faceId}
            similarity={topMatch.similarity}
            {...testid(dataTestId)}
          />
          {tab === 'attributes' && (
            <div className={st.AttributesContent}>
              <TopMatchFaceAttributes
                className={st.Attributes}
                faceId={faceId}
                {...testid(dataTestId)}
              />
            </div>
          )}
          {tab === 'additional_properties' && (
            sampleId ? (
              <AdditionalPropertiesBySampleId
                className={st.AdditionalProperties}
                sampleId={sampleId}
                {...testid(dataTestId)}
              />
            ) : (
              <span className={st.Empty}>{t('no_data')}</span>
            )
          )}
        </div>
      </div>
    </div>
  );
}

TopMatchCardPoint.displayName = displayName;

export { TopMatchCardPoint };
